import {useState} from "react";
import {useMediaQuery} from "react-responsive";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import {SidebarSection} from "./SidebarSection";
import {BarFilter} from "./BarFilter";
import {guessColorDot, formatOptionPrice, sameStringSet, AVAILABILITY_LABELS, titleCase} from "./utils";
import {useListingsStore} from "../../stores/listingsStore";
import {useShallow} from "zustand/react/shallow";
import {mediaQueryForMobile} from "../HomePage/HomePage";

// Availability is a real backend facet like New/Used, Trim, etc.: its values are
// Visor availability_status codes (with counts). Multi-select, OR'd server-side.
// AVAILABILITY_LABELS is shared with the ribbon (see utils.js).
const AVAILABILITY_ORDER = ['stock', 'transit', 'build']
// Empty = no availability filter = All (On Lot + In Transit + Build). Defaulting to
// All keeps the availability facet counts and the result list in agreement: a
// non-empty default would hide (yet still count) the excluded status — e.g. a
// future model year whose only matches are In Transit.
const DEFAULT_AVAILABILITY = []

export function Sidebar() {
    const [extMode, setExtMode] = useState("general");
    const [intMode, setIntMode] = useState("general");

    // On mobile the filters collapse behind a toggle so they don't push the
    // results far down the page. On desktop the card is always visible.
    const isMobile = useMediaQuery({query: mediaQueryForMobile})
    const [filtersOpen, setFiltersOpen] = useState(false)
    const showFilters = !isMobile || filtersOpen

    const {
        filters,
        options,
        setFilters,
    } = useListingsStore(
        useShallow((state) => ({
            filters: state.filters,
            options: state.options,
            setFilters: state.setFilters,
        }))
    )

    const toggleSeat = (seatNumber) => {
        if (filters.seats.length === 1 && filters.seats[0] === seatNumber){
            return
        }
        let selectedSeats = structuredClone(filters.seats)

        if (selectedSeats.includes(seatNumber)) {
            selectedSeats = selectedSeats.filter((s) => s !== seatNumber)
        } else {
            selectedSeats.push(seatNumber)
        }
        setFilters({seats: selectedSeats}, false, 'seats')
    };

    const toggleItem = (item, filterField) => {
        const current = filters[filterField] || []
        const newFilters = current.includes(item)
            ? current.filter(c => c !== item)
            : [...current, item]
        // Single facet toggle — let the store skip/keep this facet's own counts.
        setFilters({[filterField]: newFilters}, false, filterField)
    }

    const extFilterColorField = extMode === "general" ? "general_exterior_color_filters" : "specific_exterior_color_filters"
    const intFilterColorField = intMode === "general" ? "general_interior_color_filters" : "specific_interior_color_filters"

    const exteriorColorOptions = extMode === "general" ? options.general_exterior_color_options : options.specific_exterior_color_options
    const exteriorColorFilters = filters[extFilterColorField]

    const interiorColorOptions = intMode === "general" ? options.general_interior_color_options : options.specific_interior_color_options
    const interiorColorFilters = filters[intFilterColorField]

    // Trim (= Visor's `version`) options, sorted lowest → highest MSRP. Buckets
    // without an MSRP sort to the end (Infinity) so the list still renders.
    const trimOptions = [...(options.version_options || [])].sort(
        (a, b) => (a.msrp ?? Infinity) - (b.msrp ?? Infinity)
    )

    // The facet lists are rebuilt from every search response, so a value the
    // user has checked can drop out once its count hits 0. Re-append any such
    // selected value (as a 0-count row) so it stays visible and un-selectable.
    const withSelected = (facetOptions, selected) => {
        const opts = facetOptions || []
        if (!selected?.length) return opts
        const present = new Set(opts.map((o) => o.item))
        const missing = selected
            .filter((v) => !present.has(v))
            .map((v) => ({item: v, count: 0}))
        return missing.length ? [...opts, ...missing] : opts
    }

    // The sidebar filters (list facets + a non-default availability). searchTags,
    // location and sort are the base search, not counted/cleared here.
    const listFilterFields = [
        'inventory_types', 'years', 'versions', 'engines', 'body_types',
        'general_exterior_color_filters', 'general_interior_color_filters',
        'specific_exterior_color_filters', 'specific_interior_color_filters',
        'options', 'features', 'seats',
    ]
    // Trim (= Visor's `version`) options are sorted above; availability always
    // shows all three phases in a fixed On Lot → In Transit → Building order
    // (rather than by count). Any phase the server omitted because it had no
    // matches is added as a 0-count row so e.g. "Building 0" stays visible — this
    // only reuses the existing stock/transit/build codes, no new backend value.
    const availabilityFacet = withSelected(options.availability_status_options, filters.availability_statuses)
    const availabilityByItem = new Map(availabilityFacet.map((o) => [o.item, o]))
    const availabilityOptions = AVAILABILITY_ORDER.map(
        (code) => availabilityByItem.get(code) || {item: code, count: 0}
    )

    // Year options newest → oldest (sorted after the withSelected merge so any
    // re-appended 0-count selection still lands in year order).
    const yearOptions = withSelected(options.year_options, filters.years)
        .slice()
        .sort((a, b) => Number(b.item) - Number(a.item))

    // Hidden only when the provider has no body styles and none is selected.
    const bodyTypeOptions = withSelected(options.body_type_options, filters.body_types)

    // Availability counts as an active filter when it differs from the default
    // (All / empty) — compared as a set so order and the empty default are handled.
    const availabilityChanged = !sameStringSet(filters.availability_statuses || [], DEFAULT_AVAILABILITY)
    const filterCount =
        listFilterFields.reduce((n, f) => n + (filters[f]?.length || 0), 0) +
        (availabilityChanged ? 1 : 0)

    const clearFilters = () => {
        const cleared = listFilterFields.reduce((acc, f) => ({...acc, [f]: []}), {})
        cleared.availability_statuses = DEFAULT_AVAILABILITY
        setFilters(cleared, true)
    }

    const colorDot = (item) => (
        <span className="color-dot" style={{background: guessColorDot(item.item)}}/>
    )

    // Color section header: two tabs (brand-general vs brand-specific colors).
    // `generalLabel` names the general tab per section ("Exterior Color" /
    // "Interior Color"); the specific tab is always "Specific Color".
    // stopPropagation so clicking a tab switches mode without collapsing.
    const colorTabs = (mode, onSwitch, generalLabel) => (
        <div className="tab-btn-group" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                className={`tab-btn ${mode === "general" ? "active" : ""}`}
                onClick={() => onSwitch("general")}
            >
                {generalLabel}
            </button>
            <button
                type="button"
                className={`tab-btn ${mode === "specific" ? "active" : ""}`}
                onClick={() => onSwitch("specific")}
            >
                Specific Color
            </button>
        </div>
    )

    return (
        <aside className="sidebar">
            {
                isMobile &&
                <button
                    type="button"
                    className="sidebar-filters-toggle"
                    onClick={() => setFiltersOpen(o => !o)}
                >
                    <span className="sidebar-filters-toggle-label">
                        <TuneIcon fontSize="small"/> Filters
                    </span>
                    <ExpandMoreIcon className={`chevron-icon ${filtersOpen ? 'open' : ''}`}/>
                </button>
            }
            {
                showFilters &&
            // Filter groups laid out as a responsive grid of individual cards:
            // compact facets (filter-box-sm) sit two-up, long lists
            // (filter-box-lg) span the full width. The grouping/order is purely
            // presentational — every SidebarSection/BarFilter and its handlers are
            // unchanged from the previous stacked layout.
            <div className="filter-boxes-wrap">
                <div className="sidebar-filters-head">
                    <span className="sidebar-filters-head-title">
                        Filters
                        {filterCount > 0 && <span className="sidebar-filters-count">{filterCount}</span>}
                    </span>
                    {
                        filterCount > 0 &&
                        <button type="button" className="sidebar-clear-btn" onClick={clearFilters}>
                            Clear all
                        </button>
                    }
                </div>

                <div className="sidebar-card filter-box-sm">
                    <SidebarSection title="Availability" visibleRows={3}>
                        <BarFilter
                            items={availabilityOptions}
                            minRows={3}
                            getLabel={(o) => AVAILABILITY_LABELS[o.item] || o.item}
                            isSelected={(item) => (filters.availability_statuses || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'availability_statuses')}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-sm">
                    <SidebarSection title="New / Used" visibleRows={3}>
                        <BarFilter
                            items={withSelected(options.inventory_type_options, filters.inventory_types)}
                            minRows={3}
                            getLabel={(o) => titleCase(o.item)}
                            isSelected={(item) => (filters.inventory_types || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'inventory_types')}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-sm">
                    <SidebarSection title="Year" visibleRows={3}>
                        <BarFilter
                            items={withSelected(options.year_options, filters.years)}
                            minRows={3}
                            isSelected={(item) => (filters.years || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'years')}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-sm">
                    <SidebarSection title="# of Seats" visibleRows={3}>
                        <BarFilter
                            items={(options.seat_capacity_options || []).map((s) => (typeof s === 'object' ? s : {item: s}))}
                            minRows={3}
                            getLabel={(o) => (o.item === '8' ? '8+' : o.item)}
                            isSelected={(item) => filters.seats.includes(item)}
                            onToggle={(item) => toggleSeat(item)}
                        />
                    </SidebarSection>
                </div>

                {
                    bodyTypeOptions.length > 0 &&
                    <div className="sidebar-card filter-box-lg">
                        <SidebarSection
                            title="Body Style"
                            expandable
                            items={bodyTypeOptions}
                            isSelected={(item) => (filters.body_types || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'body_types')}
                        >
                            <BarFilter
                                items={bodyTypeOptions}
                                isSelected={(item) => (filters.body_types || []).includes(item)}
                                onToggle={(item) => toggleItem(item, 'body_types')}
                            />
                        </SidebarSection>
                    </div>
                }

                <div className="sidebar-card filter-box-lg">
                    <SidebarSection
                        title="Trim"
                        expandable
                        items={withSelected(trimOptions, filters.versions)}
                        isSelected={(item) => (filters.versions || []).includes(item)}
                        onToggle={(item) => toggleItem(item, 'versions')}
                    >
                        <BarFilter
                            items={withSelected(trimOptions, filters.versions)}
                            isSelected={(item) => (filters.versions || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'versions')}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-lg">
                    <SidebarSection
                        headerClassName="tabbed-header"
                        titleContent={colorTabs(extMode, setExtMode, "Exterior Color")}
                        expandable
                        modalTitle={extMode === "general" ? "Exterior Color" : "Specific Exterior Color"}
                        items={withSelected(exteriorColorOptions, exteriorColorFilters)}
                        isSelected={(item) => exteriorColorFilters.includes(item)}
                        onToggle={(item) => toggleItem(item, extFilterColorField)}
                        renderDot={colorDot}
                    >
                        <BarFilter
                            items={withSelected(exteriorColorOptions, exteriorColorFilters)}
                            isSelected={(item) => exteriorColorFilters.includes(item)}
                            onToggle={(item) => toggleItem(item, extFilterColorField)}
                            renderDot={colorDot}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-lg">
                    <SidebarSection
                        headerClassName="tabbed-header"
                        titleContent={colorTabs(intMode, setIntMode, "Interior Color")}
                        expandable
                        modalTitle={intMode === "general" ? "Interior Color" : "Specific Interior Color"}
                        items={withSelected(interiorColorOptions, interiorColorFilters)}
                        isSelected={(item) => interiorColorFilters.includes(item)}
                        onToggle={(item) => toggleItem(item, intFilterColorField)}
                        renderDot={colorDot}
                    >
                        <BarFilter
                            items={withSelected(interiorColorOptions, interiorColorFilters)}
                            isSelected={(item) => interiorColorFilters.includes(item)}
                            onToggle={(item) => toggleItem(item, intFilterColorField)}
                            renderDot={colorDot}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-lg">
                    <SidebarSection
                        title="Installed Options"
                        expandable
                        items={withSelected(options.option_packages, filters.options)}
                        getLabel={(option) => option.name || option.item}
                        getSecondary={(option) => formatOptionPrice(option.msrp)}
                        isSelected={(item) => (filters.options || []).includes(item)}
                        onToggle={(item) => toggleItem(item, 'options')}
                    >
                        <BarFilter
                            items={withSelected(options.option_packages, filters.options)}
                            getLabel={(option) => option.name || option.item}
                            getSecondary={(option) => formatOptionPrice(option.msrp)}
                            isSelected={(item) => (filters.options || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'options')}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-lg">
                    <SidebarSection
                        title="Features"
                        expandable
                        items={withSelected(options.features, filters.features)}
                        isSelected={(item) => filters.features.includes(item)}
                        onToggle={(item) => toggleItem(item, 'features')}
                    >
                        <BarFilter
                            items={withSelected(options.features, filters.features)}
                            isSelected={(item) => filters.features.includes(item)}
                            onToggle={(item) => toggleItem(item, 'features')}
                        />
                    </SidebarSection>
                </div>

                <div className="sidebar-card filter-box-lg">
                    <SidebarSection
                        title="Engine"
                        expandable
                        items={withSelected(options.engine_options, filters.engines)}
                        isSelected={(item) => (filters.engines || []).includes(item)}
                        onToggle={(item) => toggleItem(item, 'engines')}
                    >
                        <BarFilter
                            items={withSelected(options.engine_options, filters.engines)}
                            isSelected={(item) => (filters.engines || []).includes(item)}
                            onToggle={(item) => toggleItem(item, 'engines')}
                        />
                    </SidebarSection>
                </div>
            </div>
            }
        </aside>
    );
}
